import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Headphones, Youtube, ArrowRight } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import SponsorStrip from "@/components/editorial/SponsorStrip";
import { fetchEpisodes } from "@/lib/episodes";

const SITE_URL = "https://mujeres-motor.com";
const PODCAST_COVER = "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/c48d566a1998d4cbf4a047d77ad72e32b6bb8906e2e838d14b7d3d46d51a3802.jpeg";

export default function Podcast() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEpisodes().then((list) => { setEpisodes(list); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <SiteLayout transparent>
      <></>

      {/* Hero */}
      <section data-testid="podcast-hero" className="relative min-h-[70vh] flex items-end bg-charcoal text-cream overflow-hidden pb-16">
        <img src={PODCAST_COVER} alt="Estudio Mujeres Motor" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/20 via-charcoal/50 to-charcoal" />
        <Container className="relative z-10">
          <Eyebrow tone="gold" className="mb-5 block">Podcast</Eyebrow>
          <h1 className="font-serif font-light text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.03] max-w-4xl">
            Conversaciones completas con <span className="italic">las mujeres que están moviendo México.</span>
          </h1>
          <p className="mt-6 text-cream/75 text-lg max-w-2xl">
            Un episodio al mes, 40 a 60 minutos, sin cortes comerciales. Disponible en todas las plataformas.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="https://open.spotify.com/show/4rOoJ6Egrf8K2IrywzwOMk" target="_blank" rel="noopener noreferrer" data-testid="podcast-spotify-cta" className="inline-flex items-center gap-2 rounded-full bg-[#1DB954] text-charcoal px-6 py-3 text-sm font-medium hover:bg-[#1ed760] transition-all duration-[400ms]">
              <Headphones className="h-4 w-4" />Escuchar en Spotify
            </a>
            <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" data-testid="podcast-youtube-cta" className="inline-flex items-center gap-2 rounded-full bg-[#C4302B] text-cream px-6 py-3 text-sm font-medium hover:bg-[#9a2520] transition-all duration-[400ms]">
              <Youtube className="h-4 w-4" />Ver en YouTube
            </a>
          </div>
        </Container>
      </section>

      {/* Platform strip */}
      <section className="bg-cream py-8 border-b border-charcoal/5">
        <Container>
          <p className="text-center text-xs uppercase tracking-[0.28em] text-charcoal/60">
            Disponible en <span className="text-charcoal font-medium">Spotify · YouTube · Apple Podcasts</span>
          </p>
        </Container>
      </section>

      {/* Episodes grid */}
      <section data-testid="podcast-grid" className="bg-cream py-20 md:py-28">
        <Container>
          <div className="mb-12 max-w-2xl">
            <Eyebrow tone="terracotta" className="mb-4 block">Temporada 1</Eyebrow>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.08]">Los episodios.</h2>
          </div>
          {loading ? (
            <p className="text-charcoal/50">Cargando episodios…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {episodes.map((ep) => (
                <Link key={ep.id} to={`/podcast/${ep.slug}`} data-testid={`episode-card-${ep.slug}`} className="group flex flex-col">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-charcoal-soft mb-5">
                    {ep.cover_image_url && (
                      <img src={ep.cover_image_url} alt={ep.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 right-4 flex justify-between text-[10px] uppercase tracking-[0.28em] text-cream">
                      <span>EP {String(ep.number).padStart(2, "0")}</span>
                      <span>{ep.duration_minutes} min</span>
                    </div>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-2">{ep.season}</p>
                  <h3 className="font-serif text-2xl text-charcoal font-light leading-tight mb-2 group-hover:text-terracotta transition-colors">{ep.title}</h3>
                  <p className="text-sm text-charcoal/65">{ep.guest_name}{ep.guest_role ? ` · ${ep.guest_role}` : ""}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-terracotta">Escuchar <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" /></span>
                </Link>
              ))}
            </div>
          )}

          {/* Founding mention small */}
          <div className="mt-24">
            <SponsorStrip tier="founding" eyebrow="Temporada 1 hecha posible por" tone="light" size="md" centered testid="podcast-founding-mention" />
          </div>
        </Container>
      </section>

      {/* Newsletter */}
      <section className="bg-cream-dark py-20 border-t border-charcoal/5">
        <Container>
          <div className="text-center max-w-xl mx-auto">
            <Eyebrow tone="terracotta" className="mb-4 block">Suscríbete</Eyebrow>
            <h3 className="font-serif font-light text-3xl md:text-4xl mb-4">Recibe cada episodio nuevo.</h3>
            <p className="text-charcoal/70 mb-6">El primer lunes de cada mes. Ensayo editorial corto incluido.</p>
            <Link to="/community" className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-6 py-3 text-sm hover:bg-[#A64826]">
              Únete a la comunidad <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}
